import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronLeft, AlertTriangle } from 'lucide-react';

const AIDisclosuresPage = () => {
  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>AI Disclosures - Sortio</title>
        <meta name="description" content="Disclosures about Sortio's AI features: which models, what data is sent, retention, limitations, and how to opt out." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Sparkles className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            AI <span className="apple-gradient-text">Disclosures</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            Required disclosures about the AI features in Sortio, what data is sent to AI providers, and how to opt out. Provided in compliance with Article 50 of the EU AI Act.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10 text-apple-300 leading-relaxed">
            <p className="text-sm text-apple-400"><em>Last updated: 25 September 2026</em></p>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">AI features in Sortio</h2>
              <p>Sortio uses generative AI in the following ways:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Sorting suggestions.</strong> Given a folder of files and a natural-language prompt, Sortio proposes destination folders and renames for each file.</li>
                <li><strong>Chat assistant.</strong> An in-app assistant answers questions about your files, your sort history, and how to use the product.</li>
                <li><strong>Knowledge graph entity extraction.</strong> Optional. When enabled, Sortio identifies entities (people, organizations, projects, matters) inside selected files and builds a local knowledge graph that improves matter and client matching.</li>
                <li><strong>Integration matchers.</strong> For Clio Manage and similar integrations, Sortio matches filenames against your matter list using AI.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Models we use</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>OpenAI GPT family</strong> (default cloud provider). Inference is performed under OpenAI's API terms; current terms state that API content is not used to train OpenAI's models.</li>
                <li><strong>Anthropic Claude</strong> (optional, via Bring-Your-Own-Key). Used only if you supply an Anthropic API key in Settings.</li>
                <li><strong>Local LLM via Ollama</strong> (optional). When enabled, prompts and content stay on your machine and do not transit Sortio's servers or any third party.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">What data is sent</h2>
              <p>By default, with cloud inference selected, the following leaves your device:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Filenames of the files you are sorting.</li>
                <li>Folder paths of the source and candidate destinations.</li>
                <li>Your prompt and our system prompts.</li>
                <li>Matter metadata such as display number, description, client names, and party names (Clio integration only).</li>
              </ul>
              <p>
                File <strong>contents</strong> are sent only if you explicitly turn on a content-sorting feature (such as AI rename by content, content-based matching, or knowledge graph indexing). With those features off, only filenames and folder paths leave your device.
              </p>
              <p>
                If you choose a local LLM (Ollama), nothing in the list above leaves your machine.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Retention</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Sort requests you run with cloud inference (filenames, folder paths, your instruction and the resulting plan) are kept for 30 days for service operation and debugging.</li>
                <li>Sortio's classifier service retains prompts for up to 30 days for service operation and debugging, after which client names, matter party names, and other identifying entity strings are redacted before further retention.</li>
                <li>OpenAI processes API content under its own data processing terms; current terms commit to not training on API data and to short-term retention for abuse prevention. We cannot guarantee third-party practices and you should review each provider's current terms if their handling matters to your use case.</li>
                <li>Anthropic, when used via BYOK, processes content under its own terms.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Limitations and accuracy</h2>
              <div className="p-4 rounded-lg bg-apple-600/20 border border-apple-600/30 flex items-start">
                <AlertTriangle className="w-5 h-5 text-sortio-accent mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p className="text-sm">
                  AI suggestions are not always correct. Sortio shows a preview of every proposed move and rename before any change is applied so you can review and reject suggestions. Always keep backups before running large sorts.
                </p>
              </div>
              <p>
                Known limitations include: filename misinterpretation when names are ambiguous; entity extraction errors on heavily abbreviated or boilerplate text; degraded quality on languages other than English; occasional hallucinated dates or amounts when content sorting is enabled. We continually evaluate model performance and switch defaults when a newer model meaningfully improves accuracy. The most recent benchmark write-up is on our blog at{' '}
                <Link to="/articles/ai-model-upgrade" className="text-sortio-primary hover:text-sortio-primary/80">AI model upgrade</Link>.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">How to opt out</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Use a local LLM.</strong> In Sortio, go to <strong>Settings, AI, Provider</strong> and select Ollama. Prompts and content then stay on your machine.</li>
                <li><strong>Use BYOK with Anthropic.</strong> Supply your own Anthropic API key; the data is processed under your Anthropic contract, not Sortio's.</li>
                <li><strong>Turn off content sorting.</strong> Keep AI sort and rename limited to filenames and folder paths; do not enable content-based features.</li>
                <li><strong>Disable AI entirely.</strong> Use rule-based sorting (Rule Builder) for fully deterministic, AI-free sorting.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">No automated decision making with legal effect</h2>
              <p>
                Sortio's AI features classify and rename files at your request. They do not produce decisions about you with legal or similarly significant effects under GDPR Article 22.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Questions</h2>
              <p>
                Contact <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDisclosuresPage;
